// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/HomeStay.tsx
// 🔧 PURPOSE: Le viste S e A della Home: il soggiorno (dal giorno
//             dell'arrivo alle 15:00 di Roma fino al giorno della partenza)
//             e il dopo (il giorno dopo la partenza, finché dura la
//             sessione: recensione e "prenota di nuovo", la casa non si
//             comanda più).
//
//             Props (HomeStayProps = HomeBookedViewProps, components/home/types):
//             - state: HomeState (view 'stay' o 'after': le date di Roma
//               per la notte (stayDayLabel), isEve, isCheckoutDay, checkoutTime,
//               checkInDone, taxDue, transferBooked, homeAvailable,
//               doorCodeAvailable, greeting, stayEnded...);
//             - now: l'ora del clock della Home (useNow);
//             - token: il token della sessione;
//             - booking: la prenotazione;
//             - guestName: il primo nome, o null;
//             - catalog: il catalogo degli extra (null finché non arriva;
//               controllare catalog.success).
//             Login, chat e assistente da useHomeSession(); finestre dei
//             servizi da useServiceDialogs().
//
//             Ordine S (mockup Main): intestazione (saluto dall'ora di Roma,
//             "Notte 3 di 7 · Appartamento"; in S e in A con la foto
//             dell'appartamento, booking.apartmentPhotoUrl) + StayCard con QuickActions
//             (codice porta, WiFi, Reception 24h); Adesso per te; Extra
//             (Cibo e bevande in evidenza, HomeExtras); Scopri (DiscoverRail
//             'stay'); Servizi (UsefulServices 'stay' con la griglia delle
//             esperienze); Per il prossimo viaggio (SpecialOffers 'stay');
//             Intrattenimento SUBITO SOPRA la reception; ReceptionCard
//             'full'; footer.
//
//             Ordine A: "GRAZIE DELLA VISITA", StayCard 'after' senza
//             tessere; Com'è andata? (recensione, torna a trovarci); offerte,
//             Scopri, Info utili (parcheggio, farmacia, lavanderie,
//             emergenze: l'ospite può essere ancora in zona), Intrattenimento,
//             reception, footer. In A non c'è niente che comandi la casa: né
//             tessere (codice, WiFi), né "La tua casa", né extra, né i
//             servizi in casa (ritiro rifiuti).
//             La chat con la reception resta: non è un comando della casa.
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { HomeBookedViewProps } from './types';
import { stayDayLabel } from '../../lib/homeState';
import HomeMasthead from './HomeMasthead';
import StayCard from './StayCard';
import QuickActions from './QuickActions';
import NowForYou from './NowForYou';
import HomeExtras from './HomeExtras';
import UsefulServices from './UsefulServices';
import DiscoverRail from './DiscoverRail';
import ReceptionCard from './ReceptionCard';
import Entertainment from './Entertainment';
import HomeFooter from './HomeFooter';
import SpecialOffers from '../SpecialOffers';

export type HomeStayProps = HomeBookedViewProps;

const GREETING_KEYS = {
  morning: 'home.masthead.greetMorning',
  afternoon: 'home.masthead.greetAfternoon',
  evening: 'home.masthead.greetEvening',
} as const;

const HomeStay: React.FC<HomeStayProps> = ({ state, now, token, booking, guestName, catalog }) => {
  const { t } = useTranslation();
  const after = state.view === 'after';

  // "Notte 3 di 7 · Appartamento 101" (la vigilia "Ultima notte", con una
  // notte sola "Una notte con noi · partenza domani", il giorno della
  // partenza "Giorno della partenza"); dopo, solo l'appartamento. Per un
  // gruppo i nomi degli appartamenti sono tanti: si tagliano, la notte resta
  // sempre in vista.
  const dayLabel = after ? null : stayDayLabel(state);
  const day = dayLabel
    ? dayLabel.key === 'home.stay.nightOf'
      ? t(dayLabel.key, { night: dayLabel.night, total: dayLabel.total })
      : t(dayLabel.key)
    : '';
  const apartment = booking.apartmentName || '';
  const sub = day || apartment ? (
    <span className="flex min-w-0">
      {day ? <span className="flex-shrink-0 whitespace-nowrap">{day}</span> : null}
      {day && apartment ? <span className="flex-shrink-0 whitespace-pre"> · </span> : null}
      {apartment ? <span className="min-w-0 truncate">{apartment}</span> : null}
    </span>
  ) : undefined;

  return (
    <>
      <HomeMasthead
        eyebrow={after ? t('home.masthead.thanks') : t(GREETING_KEYS[state.greeting])}
        title={guestName ? t('home.masthead.hello', { name: guestName }) : t('home.masthead.helloNoName')}
        sub={sub}
        photoUrl={booking.apartmentPhotoUrl}
      />
      <StayCard state={state}>
        {/* Dopo la partenza niente tessere: codice e WiFi non servono più. */}
        {after ? null : <QuickActions state={state} token={token} booking={booking} />}
      </StayCard>
      <NowForYou state={state} now={now} booking={booking} catalog={catalog} />
      {after ? (
        <>
          <SpecialOffers variant="after" />
          <DiscoverRail variant="after" />
          <UsefulServices variant="after" />
        </>
      ) : (
        <>
          <HomeExtras state={state} now={now} catalog={catalog} />
          <DiscoverRail variant="stay" />
          <UsefulServices variant="stay" homeAvailable={state.homeAvailable} stayOver={state.stayOver} />
          <SpecialOffers variant="stay" />
        </>
      )}
      <Entertainment />
      <ReceptionCard variant="full" />
      <HomeFooter />
    </>
  );
};

export default HomeStay;
